/**
 * Invoice Service (Frontend)
 *
 * API-Client für Rechnungsverwaltung
 * - Rechnungen abrufen (Admin & User)
 * - Rechnungen erstellen/bearbeiten (Admin)
 * - PDF generieren/herunterladen
 * - Vorschau anzeigen
 */

import apiClient from '../api/apiClient';
import { formatCurrency } from '../../utils/formatUtils';

// ============================================
// TYPES
// ============================================

export interface Invoice {
  id: number;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'partially_paid' | 'failed' | 'refunded';
  customer_name: string;
  customer_email: string;
  customer_address?: string;
  customer_city?: string;
  customer_postal_code?: string;
  customer_country?: string;
  customer_vat_id?: string;
  subtotal: number;
  tax_amount?: number;
  total: number;
  currency: string;
  payment_terms?: string;
  notes?: string;
  footer_text?: string;
  pdf_url?: string;
  is_locked: boolean;
  sent_at?: string;
  paid_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  credited_invoice_id?: number;
  stripe_payment_intent_id?: string;
  stripe_charge_id?: string;
  payment_method?: string;
  internal_notes?: string;
  transaction_fee?: number;
  appointment_id?: number;
  created_at: string;
  updated_at: string;
}

export interface InvoiceItem {
  id?: number;
  invoice_id?: number;
  position: number;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  created_at?: string;
  updated_at?: string;
}

export interface InvoiceWithDetails extends Invoice {
  items: InvoiceItem[];
  company_info?: {
    company_name: string;
    vat_id: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
    logo_path?: string;
  };
}

export interface CreateInvoiceData {
  issue_date: string;
  due_date: string;
  customer_name: string;
  customer_email: string;
  customer_address?: string;
  customer_city?: string;
  customer_postal_code?: string;
  customer_country?: string;
  customer_vat_id?: string;
  currency?: string;
  payment_terms?: string;
  notes?: string;
  footer_text?: string;
  items: Omit<InvoiceItem, 'id' | 'invoice_id' | 'created_at' | 'updated_at'>[];
}

export interface InvoiceFilters {
  status?: string;
  payment_status?: string;
  customer?: string;
  category?: string; // 'appointments', 'blog', 'fcms', 'academy', 'other'
  date_from?: string;
  date_to?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface InvoiceListResponse {
  invoices: Invoice[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

/**
 * Get all invoices (Admin only)
 */
export async function getInvoices(filters?: InvoiceFilters): Promise<InvoiceListResponse> {
  const params = new URLSearchParams();

  if (filters?.status) params.append('status', filters.status);
  if (filters?.payment_status) params.append('payment_status', filters.payment_status);
  if (filters?.customer) params.append('customer', filters.customer);
  if (filters?.category) params.append('category', filters.category);
  if (filters?.date_from) params.append('from_date', filters.date_from);
  if (filters?.date_to) params.append('to_date', filters.date_to);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const url = `/admin/invoices${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url);
  return response.data;
}

/**
 * Get single invoice with details (Admin)
 */
export async function getInvoiceById(id: number): Promise<InvoiceWithDetails> {
  const response = await apiClient.get(`/admin/invoices/${id}`);
  return response.data.invoice;
}

/**
 * Create new invoice (Admin)
 */
export async function createInvoice(data: CreateInvoiceData): Promise<Invoice> {
  const response = await apiClient.post('/admin/invoices', data);
  return response.data.invoice;
}

/**
 * Update invoice (Admin, only drafts)
 */
export async function updateInvoice(id: number, data: Partial<CreateInvoiceData>): Promise<Invoice> {
  const response = await apiClient.put(`/admin/invoices/${id}`, data);
  return response.data.invoice;
}

/**
 * Delete invoice (Admin, only drafts)
 */
export async function deleteInvoice(id: number): Promise<void> {
  await apiClient.delete(`/admin/invoices/${id}`);
}

/**
 * Cancel invoice with credit note (Admin)
 */
export async function cancelInvoice(id: number, reason: string): Promise<Invoice> {
  const response = await apiClient.post(`/admin/invoices/${id}/cancel`, { reason });
  return response.data.credit_note;
}

/**
 * Generate PDF for invoice (Admin)
 */
export async function generateInvoicePDF(id: number): Promise<{ pdf_url: string; url?: string }> {
  const response = await apiClient.post(`/admin/invoices/${id}/generate-pdf`);
  return response.data;
}

/**
 * Get HTML preview of invoice (Admin)
 */
export async function getInvoicePreview(id: number): Promise<string> {
  const response = await apiClient.get(`/admin/invoices/${id}/preview`);
  return response.data; // Returns HTML string
}

/**
 * Get sample invoice preview (Admin)
 */
export async function getSampleInvoicePreview(data?: Partial<CreateInvoiceData>): Promise<string> {
  const response = await apiClient.post('/admin/invoices/preview-sample', data || {});
  return response.data; // Returns HTML string
}

/**
 * Peek at next invoice number (Admin)
 */
export async function peekNextInvoiceNumber(year?: number): Promise<{ next_number: string; year: number }> {
  const params = year ? `?year=${year}` : '';
  const response = await apiClient.get(`/admin/invoices/numbers/peek${params}`);
  return response.data;
}

/**
 * Get all invoice number sequences (Admin)
 */
export async function getInvoiceNumberSequences(): Promise<any[]> {
  const response = await apiClient.get('/admin/invoices/numbers/sequences');
  return response.data.sequences;
}

// ============================================
// USER ENDPOINTS
// ============================================

/**
 * Get user's own invoices
 */
export async function getMyInvoices(): Promise<Invoice[]> {
  const response = await apiClient.get('/invoices/my');
  return response.data.invoices;
}

/**
 * Get single invoice (User - only own invoices)
 */
export async function getMyInvoiceById(id: number): Promise<InvoiceWithDetails> {
  const response = await apiClient.get(`/invoices/${id}`);
  return response.data.invoice;
}

/**
 * Download invoice PDF (User - only own invoices)
 * Opens PDF in new window
 */
export function downloadInvoicePDF(id: number): void {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5555';
  const url = `${baseUrl}/invoices/${id}/download`;

  // Open in new window with auth header
  const link = document.createElement('a');
  link.href = `${url}?token=${token}`;
  link.target = '_blank';
  link.download = `invoice-${id}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Download invoice PDF (Admin version)
 * Opens PDF in new window
 */
export function downloadInvoicePDFAdmin(invoiceNumber: string): void {
  const token = localStorage.getItem('token');
  // Extract ID from invoice number if needed, or use invoice number directly
  // For now, we'll use the invoice number approach

  // We need to get the PDF path from the invoice data
  // This is a placeholder - in reality, you'd fetch the invoice first to get the PDF path
  console.warn('Admin PDF download: implement proper path resolution');
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format invoice status for display
 */
export function formatInvoiceStatus(status: string): string {
  const statusMap: Record<string, string> = {
    draft: 'Entwurf',
    sent: 'Versendet',
    paid: 'Bezahlt',
    overdue: 'Überfällig',
    cancelled: 'Storniert'
  };
  return statusMap[status] || status;
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    draft: 'gray',
    sent: 'blue',
    paid: 'green',
    overdue: 'red',
    cancelled: 'gray'
  };
  return colorMap[status] || 'gray';
}

// NOTE: formatCurrency moved to utils/formatUtils.ts (imported above, re-exported here)
export { formatCurrency };

/**
 * Format date for display
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

/**
 * Check if invoice is editable
 */
export function isInvoiceEditable(invoice: Invoice): boolean {
  return invoice.status === 'draft' && !invoice.is_locked;
}

/**
 * Check if invoice is cancellable
 */
export function isInvoiceCancellable(invoice: Invoice): boolean {
  return invoice.status !== 'cancelled' && invoice.status !== 'draft';
}

export default {
  // Admin
  getInvoices,
  getInvoiceById,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  cancelInvoice,
  generateInvoicePDF,
  getInvoicePreview,
  getSampleInvoicePreview,
  peekNextInvoiceNumber,
  getInvoiceNumberSequences,

  // User
  getMyInvoices,
  getMyInvoiceById,
  downloadInvoicePDF,

  // Helpers
  formatInvoiceStatus,
  getStatusColor,
  formatCurrency,
  formatDate,
  isInvoiceEditable,
  isInvoiceCancellable
};
